import * as math from 'mathjs';

class CheckAR {
    /**
     * Checks if joint angles q are within the working range (qmin to qmax).
     * For rotational joints, tests different multiples of 2π to find the closest valid solution.
     * Works on a local copy of q (matching MATLAB's copy-on-write behavior).
     * 
     * @param {Array|Matrix} q - Joint angles to check
     * @param {Array|Matrix} qmin - Minimum joint angles
     * @param {Array|Matrix} qmax - Maximum joint angles
     * @param {Array|Matrix} q_alt - Previous joint angles (for finding closest 2π multiple)
     * @param {number} N - Number of joints
     * @param {Array} hh - Joint types (1 = rotational, 0 = prismatic)
     * @returns {number} out_range - 0 if in range, 1 if out of range
     */
    run(q, qmin, qmax, q_alt, N, hh) {
        // Hier wird geprüft, ob die Lösung q im Arbeitsbereich liegt, bzw. bei rotat. Gelenken ein
        // Vielfaches von 2*pi ist und in den Arbeitsbereich gebracht werden kann!
        // W. Weber, Oktober 2025

        let eps_q = 1e-7;
        let out_range = 0; // Startwert: Lösung im AR
        
        // Convert to arrays if needed
        q = math.squeeze(q);
        qmin = math.squeeze(qmin);
        qmax = math.squeeze(qmax);
        q_alt = math.squeeze(q_alt);
        
        // Ensure all inputs are arrays
        const toArray = (val) => {
            if (math.typeOf(val) === 'Matrix') {
                return val.toArray();
            }
            return Array.isArray(val) ? val : [val];
        };
        
        // Create local copy of q (MATLAB uses copy-on-write, so modifications don't affect original)
        q = [...toArray(q)]; // Spread operator creates a shallow copy
        qmin = toArray(qmin);
        qmax = toArray(qmax);
        q_alt = toArray(q_alt);

        for (let ig = 0; ig < N; ig++) { // ig: Gelenknummer (0-based in JS)
            if (hh[ig] === 1) { // =1: Drehgelenk (rotational joint)
                // q_bereich=zeros(5,1); q_test=ones(5,1)*100 - Vorbesetzungen für ein Gelenk
                let q_bereich = [0, 0, 0, 0, 0];
                let q_test = [100, 100, 100, 100, 100];
                
                // hier wird getestet q(ig,1)-4*pi, q(ig,1)-2*pi, q(ig,1), q(ig,1)+2*pi, q(ig,1)+4*pi
                let i2 = 0; // Startwert für Zahl der Lösungen im AR für das Drehgelenk hier
                
                for (let i1 = 0; i1 < 5; i1++) { // i1 Zahl der um +- 2*pi verschobenen Lösungen
                    // MATLAB: q_bereich(i1,1) = q(ig,1) + (i1-3)*2*pi
                    // i1 in MATLAB: 1,2,3,4,5 -> (i1-3): -2,-1,0,1,2
                    // i1 in JS: 0,1,2,3,4 -> (i1-2): -2,-1,0,1,2
                    q_bereich[i1] = q[ig] + (i1 - 2) * 2 * Math.PI;
                    
                    // if (q_bereich(i1,1) >= qmin(ig,1)-eps_q) & (q_bereich(i1,1) <= qmax(ig,1)+eps_q)
                    if (q_bereich[i1] >= (qmin[ig] - eps_q) && q_bereich[i1] <= (qmax[ig] + eps_q)) {
                        q_test[i1] = q_bereich[i1];
                        i2 = i2 + 1;
                    }
                }
                
                if (i2 > 0) {
                    // [Abstand,i_ab] = min(abs(q_test-q_alt(ig,1)))
                    // Find the index of the minimum absolute difference
                    let min_abstand = Infinity;
                    let i_ab = 0;
                    for (let i = 0; i < 5; i++) {
                        if (q_test[i] !== 100) {
                            let abstand = Math.abs(q_test[i] - q_alt[ig]);
                            if (abstand < min_abstand) {
                                min_abstand = abstand;
                                i_ab = i;
                            }
                        }
                    }
                    
                    // q(ig,1) = q_test(i_ab,1)
                    // Hier wird nun die Wertzuweisung aus - k*2*pi bis k*2+pi gewählt, 
                    // die am nächsten zur alten Lösung qalt(ig,1) ist
                    q[ig] = q_test[i_ab];
                } else {
                    out_range = 1; // die Lösung für das Drehgelenk IG liegt nicht im AR
                }
            } else { // hier hh(ig) = 0 --> Schubgelenk (prismatic joint)
                // if (q(ig,1) <= qmin(ig,1)-eps_q) | (q(ig,1) >= qmax(ig,1)+eps_q)
                if (q[ig] <= (qmin[ig] - eps_q) || q[ig] >= (qmax[ig] + eps_q)) {
                    out_range = 1; // das Schubgelenk ig liegt nicht im AR
                }
            }
        }

        return out_range;
    }
}

export default CheckAR;

